import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalLoading } from '../../context/GlobalLoadingContext';
import TypingAnimation from './TypingAnimation';
import OutlookEnvelope from './OutlookEnvelope';

const UnifiedLoadingScreen: React.FC = () => {
  const { state } = useGlobalLoading();
  const { t } = useTranslation();

  // Loading messages that will be animated - memoized to prevent recreating on each render
  const loadingMessages = useMemo(() => [
    t('common.loadingMessages.verifyingUser'),
    t('common.loadingMessages.gatheringMeetings'),
    t('common.loadingMessages.preparingConversations'),
    t('common.loadingMessages.organizingLeads'),
    t('common.loadingMessages.syncingContacts'),
    t('common.loadingMessages.updatingPipeline'),
    t('common.loadingMessages.loadingDashboard')
  ], [t]);

  // Debug logging to track component lifecycle and cleanup animation states
  React.useEffect(() => {
    // console.log('UnifiedLoadingScreen component mounted/remounted');
    return () => {
      // console.log('UnifiedLoadingScreen component unmounted - cleaning animation states');
      // Clear all animation states when loading screen unmounts (loading completed)
      try {
        sessionStorage.removeItem('envelope-animation-start');
        sessionStorage.removeItem('envelope-animation-session');
        sessionStorage.removeItem('envelope-page-loaded');
        sessionStorage.removeItem('typing-animation-state');
        console.log('Animation states cleared for next loading session');
      } catch (e) {
        console.warn('Failed to clear animation states:', e);
      }
    };
  }, []);

  return (
    <div className="h-screen w-full bg-white flex flex-col">
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center max-w-md w-full px-8">
          {/* Outlook-style Envelope Animation */}
          <div className="mb-12">
            <div className="mx-auto mb-6">
              <OutlookEnvelope />
            </div>
          </div>

          {/* Typing Animation */}
          <div className="mb-8">
            <TypingAnimation
              key="loading-animation"
              messages={loadingMessages}
              typingSpeed={30}
              pauseDuration={200}
              deletingSpeed={30}
              emptyPauseDuration={100}
            />
          </div>

          {/* Error Messages */}
          {state.errors.length > 0 && (
            <div className="bg-red-100 border border-red-400 p-4 mb-4">
              <div className="flex">
                <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="ml-3">
                  <p className="text-sm text-red-700">
                    {state.errors[state.errors.length - 1]}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* SOL Media Logo at bottom */}
      <div className="pb-32 flex justify-center">
        <img 
          src="/assets/images/sol-media-logo-black.png" 
          alt="SOL MEDIA SOLUTIONS" 
          className="h-8"
        />
      </div>
    </div>
  );
};

export default UnifiedLoadingScreen;